/**
 * Multi-source usage state.
 *
 * Every connected environment answers the same typed query by scanning its own
 * Claude Code and Codex transcripts, and CMMD answers the same window from its
 * `ai_request_log`. The client merges the answers; raw transcripts never leave
 * the machine that produced them.
 *
 * @module usage/useUsage
 */
import {
  USAGE_CONTRACT_VERSION,
  type UsageSummary,
  type UsageSummaryInput,
} from "@cmmd-forge/contracts";
import { mergeUsage, type EnvironmentUsage, type MergedUsage } from "@cmmd-forge/shared/usageMerge";
import { useQueries, useQueryClient } from "@tanstack/react-query";

import { cmmdApiClient } from "~/environments/cmmdApi";
import {
  getPrimaryEnvironmentConnection,
  readEnvironmentConnection,
  useSavedEnvironmentRegistryStore,
} from "~/environments/runtime";
import { useSavedEnvironmentRuntimeStore } from "~/environments/runtime/savedEnvironmentRuntimeStore";
import { WsRequestTimeoutError } from "~/rpc/wsTransport";

/** Stable id for the CMMD-reported source, which is not an environment. */
const CMMD_USAGE_SOURCE_ID = "cmmd";

/**
 * Stable id for a primary environment that would not resolve.
 *
 * A resolved primary is listed under its own environment id. This one only
 * exists when the connection threw, which is exactly when there is no id to
 * name it by.
 */
const UNRESOLVED_PRIMARY_SOURCE_ID = "primary";

export interface UsageSourceStatus {
  readonly sourceId: string;
  readonly label: string;
  readonly isPending: boolean;
  readonly error: string | null;
  readonly summary: UsageSummary | null;
}

export interface UsageView {
  readonly merged: MergedUsage;
  readonly sources: readonly UsageSourceStatus[];
  /** True until at least one source has answered. */
  readonly isPending: boolean;
  /**
   * True while sources that have not failed are still answering. Failed sources
   * are reported through their own error rows: totals will not improve by
   * waiting on them, so they must not read as "still reporting".
   */
  readonly isPartial: boolean;
  readonly refresh: () => void;
}

interface UsageQueryTarget {
  readonly sourceId: string;
  readonly label: string;
  readonly read: () => Promise<UsageSummary>;
}

/** Serialised window, so switching ranges does not reuse another range's cache. */
function windowKey(input: UsageSummaryInput): string {
  return JSON.stringify({
    sinceDay: input.sinceDay,
    untilDay: input.untilDay,
    timeZone: input.timeZone,
    resolution: input.resolution,
    sinceTime: input.sinceTime,
    untilTime: input.untilTime,
  });
}

const USAGE_QUERY_ROOT = "usage" as const;

/**
 * How many times a timed-out read is asked again before it counts as failed.
 *
 * The transport bounds one read at 120 s, but the server's scan is not bound
 * by the request: it keeps running after the client gives up, and the next
 * request for the same window joins it. So a timeout means "still scanning",
 * and the source stays pending while the read is re-asked. Thirty tries is an
 * hour, longer than any cold scan measured (36 GB of transcripts on alex-mbp).
 */
const USAGE_TIMEOUT_RETRIES = 30;

function isUsageReadTimeout(error: unknown): boolean {
  return error instanceof WsRequestTimeoutError;
}

function errorMessage(sourceId: string): string {
  return sourceId === CMMD_USAGE_SOURCE_ID
    ? "CMMD could not report usage."
    : "This environment could not report usage.";
}

export function useUsage(input: UsageSummaryInput): UsageView {
  const queryClient = useQueryClient();
  const savedEnvironmentsById = useSavedEnvironmentRegistryStore((state) => state.byId);
  const savedEnvironmentRuntimeById = useSavedEnvironmentRuntimeStore((state) => state.byId);
  const key = windowKey(input);

  const targets = ((): readonly UsageQueryTarget[] => {
    const collected: UsageQueryTarget[] = [];
    const seen = new Set<string>();

    // The environment serving this SPA is always a source, and is not
    // necessarily in the saved registry.
    try {
      const primary = getPrimaryEnvironmentConnection();
      seen.add(primary.environmentId);
      collected.push({
        sourceId: primary.environmentId,
        label: primary.knownEnvironment.label,
        read: () => primary.client.usage.getSummary(input),
      });
    } catch (cause) {
      // The primary target falls back to the window origin, so it resolves in
      // any browser. A throw here is a real failure, and swallowing it left
      // the environment out of the target list entirely: not pending, not
      // failed, and named nowhere, so a CMMD-only total read as complete.
      // Listing it as a failed source is what makes the gap visible.
      collected.push({
        sourceId: UNRESOLVED_PRIMARY_SOURCE_ID,
        label: "This environment",
        read: () => Promise.reject(cause instanceof Error ? cause : new Error(String(cause))),
      });
    }

    for (const record of Object.values(savedEnvironmentsById)) {
      if (seen.has(record.environmentId)) continue;
      // An environment that is not connected cannot scan anything, and listing
      // it as pending would hold the page open forever.
      if (savedEnvironmentRuntimeById[record.environmentId]?.connectionState !== "connected") {
        continue;
      }
      const connection = readEnvironmentConnection(record.environmentId);
      if (!connection) continue;
      seen.add(record.environmentId);
      collected.push({
        sourceId: record.environmentId,
        label: record.label,
        read: () => connection.client.usage.getSummary(input),
      });
    }

    collected.push({
      sourceId: CMMD_USAGE_SOURCE_ID,
      label: "CMMD",
      read: () =>
        cmmdApiClient.getUsageSummary({
          sinceDay: input.sinceDay,
          untilDay: input.untilDay,
          timeZone: input.timeZone,
          // The whole window, so CMMD buckets at the resolution being drawn.
          // Spread rather than assigned: the fields are exact-optional, so a
          // day window must omit them rather than send them as undefined.
          ...(input.resolution === undefined ? {} : { resolution: input.resolution }),
          ...(input.sinceTime === undefined ? {} : { sinceTime: input.sinceTime }),
          ...(input.untilTime === undefined ? {} : { untilTime: input.untilTime }),
        }),
    });

    return collected;
  })();

  const results = useQueries({
    queries: targets.map((target) => ({
      queryKey: [USAGE_QUERY_ROOT, target.sourceId, key],
      queryFn: target.read,
      // A scan is expensive and the window is historical; refetching on every
      // focus would rescan gigabytes for an unchanged answer.
      staleTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      // A timeout is the scan outliving the request, not a failure. Anything
      // else fails at once: totals will not improve by asking again.
      retry: (failureCount: number, error: unknown) =>
        isUsageReadTimeout(error) && failureCount < USAGE_TIMEOUT_RETRIES,
      retryDelay: 0,
    })),
  });

  const sources: readonly UsageSourceStatus[] = targets.map((target, index) => {
    const result = results[index];
    return {
      sourceId: target.sourceId,
      label: target.label,
      isPending: result?.isPending ?? true,
      error: result?.isError ? errorMessage(target.sourceId) : null,
      summary: result?.data ?? null,
    };
  });

  const answered: EnvironmentUsage[] = sources.flatMap((source) =>
    source.summary === null
      ? []
      : [{ sourceId: source.sourceId, label: source.label, summary: source.summary }],
  );
  const merged = mergeUsage(answered, USAGE_CONTRACT_VERSION);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: [USAGE_QUERY_ROOT] });
  };

  const answeredCount = sources.filter((source) => source.summary !== null).length;
  const stillReporting = sources.filter(
    (source) => source.summary === null && source.error === null,
  ).length;

  return {
    merged,
    sources,
    isPending: answeredCount === 0 && stillReporting > 0,
    isPartial: answeredCount > 0 && stillReporting > 0,
    refresh,
  };
}
